import { useState, useEffect, useCallback, useRef } from 'react';
import { Asset } from '../../state/types';
import { getVehiclesFromDb } from '../../api/getVehiclesFromDb';
import { AssetFilters, ImageFilter, defaultFilters } from '../../components/vehicles/config';
import { useSession } from '../../context/SessionContext';

// Load initial state from sessionStorage
const loadInitialState = () => {
  const stored = localStorage.getItem('vehiclesNavState');
  console.log("stored",stored)
  if (!stored) return { page: 1, filters: {}, resultsPerPage: 10 };
  try {
    const parsed = JSON.parse(stored);
    return {
      page: parsed.page || 1,
      filters: parsed.filters || defaultFilters,
      resultsPerPage: parsed.resultsPerPage || 10,
    };
  } catch {
    return { page: 1, filters: defaultFilters, resultsPerPage: 10 };
  }
};

const VEHICLE_RPP_KEY = 'vehiclesRowsPerPage';

const loadRowsPerPage = () => {
  const stored = localStorage.getItem(VEHICLE_RPP_KEY);
  const n = stored ? Number(stored) : NaN;
  return [10, 25, 50, 100].includes(n) ? n : 10;
};

export const useVehicles = () => {
  const { page: initialPage, filters: initialFilters } = loadInitialState();
  const initialRPP = loadRowsPerPage();
  const { selectedSystem, setIsLoading } = useSession();

  const [vehicles, setVehicles] = useState<Asset[]>([]);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [filters, setFilters] = useState<AssetFilters>(initialFilters);
  const [resultsPerPage, setResultsPerPage] = useState(initialRPP);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedVehicles, setSelectedVehicles] = useState<Asset[]>([]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setFilters(defaultFilters);
    setPage(1);
  }, [selectedSystem]);

  useEffect(() => {
    localStorage.setItem(VEHICLE_RPP_KEY, String(resultsPerPage));
    setPage(1);
  }, [resultsPerPage]);

  const fetchVehicles = useCallback(async () => {
    setIsLoading(true);
    if (!selectedSystem) {
      setVehicles([]);
      setTotalVehicles(0);
      setIsLoading(false);
      return;
    }
    try {
      const {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
        ...otherFilters
      } = filters;

      const payload: Record<string, any> = { ...otherFilters };

      const imageFilters: Record<string, ImageFilter | undefined> = {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
      };

      for (const key in imageFilters) {
        const filter = imageFilters[key];
        if (filter?.checked) {
          payload[key] = {
            checked: true,
            operator: filter.operator || '=', // Default to '=' if not set
            imageCount: filter.imageCount ?? 0,   // Default to 0 if not set
          };
        }
      }

      const body = {
        ...payload,
        page,
        per_page: resultsPerPage,
        system_code: selectedSystem.systemCode,
        sort_direction: sortDirection,
      };
      const { vehicles: translatedVehicles, total } = await getVehiclesFromDb(body);
      setVehicles(translatedVehicles);
      setTotalVehicles(total);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSystem, filters, page, resultsPerPage, sortDirection]);

  // Effect to save navigation state to sessionStorage
  useEffect(() => {
    const savedState = { page, filters, resultsPerPage };
    localStorage.setItem('vehiclesNavState', JSON.stringify(savedState));
  }, [page, filters, resultsPerPage]);

  // Effect to fetch vehicles when dependencies change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchVehicles();
    }, 150); // Debounce to prevent rapid firing
    return () => clearTimeout(debounceTimer);
  }, [fetchVehicles]);

  // Handler for updating filters that also resets the page
  const updateFilters = (newFilters: AssetFilters) => {
    setPage(1);
    setFilters(newFilters);
  };

  // Return all the state and handlers the component will need
  return {
    vehicles,
    totalVehicles,
    page,
    setPage,
    resultsPerPage,
    setResultsPerPage,
    sortDirection,
    setSortDirection,
    filters,
    updateFilters,
    refetch: fetchVehicles, // Expose a manual refetch function
    selectedVehicles,
    setSelectedVehicles
  };
};